/*
 * Data for the critical configuration change (Project, Protocol, Bus Type),
 * assembled from existing endpoints rather than a dedicated one:
 *
 *   Projects          GET /api/battery_config_list/1          (getDigitalTwinConfigList)
 *   Protocols         GET /api/get_filter_data  protocolMeta  (getFilterData)
 *   Bus Types         GET /api/get_filter_data  vehicleConfig (getFilterData)
 *   Current IDs       GET /api/get_filter_data  configBOP, filtered to this battery
 *   Current Bus Type  vehicleInfo.vehicleSubType, already in DigitalTwinContext
 *
 * The output keeps the { current, options } shape ChangeVehicleConfigView reads.
 */
import CODE from "../../Static/Constants/StatusCodes";
import { getStringStructure } from "./StringStructure";

// Large enough to hold every battery config of an org on one page; the list
// endpoint requires a page size.
export const PROJECT_PAGE_SIZE = 1000;

export const PROTOCOL_FILTER_CONFIG = {
  type: "dynamic",
  queryParams: { table: "protocolMeta", column: "protocolName", keyColumn: "id" },
};

export const BUS_TYPE_FILTER_CONFIG = {
  type: "dynamic",
  queryParams: { table: "vehicleConfig", column: "vehicleSubType" },
};

// configID as the value and protocolID as the key, so one row carries both of
// the battery's current IDs. get_filter_data returns them as { id, value }.
export const buildCurrentConfigFilterConfig = (batteryID) => ({
  type: "dynamic",
  queryParams: {
    table: "configBOP",
    column: "configID",
    keyColumn: "protocolID",
    queryFilters: { batteryID: String(batteryID) },
  },
});

// GetData wraps the server body in `response`. A 404 from these endpoints means
// "nothing to list", which is an empty list here, not a failure.
const isUsable = (res) =>
  res?.responseStatus?.code === CODE.SUCCESS || res?.responseStatus?.code === CODE.NODATA;

export const getFilterRows = (res) => {
  const rows = res?.response?.response?.filterData;
  return Array.isArray(rows) ? rows : [];
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const toProjectOption = (row) => ({
  configID: toNumberOrNull(row?.id),
  configName: row?.configName ?? null,
  ratedCapacitySystem: row?.ratedCapacitySystem ?? null,
  buildingBlockType: row?.buildingBlockType ?? null,
  parallelPacks: row?.parallelPacks ?? null,
  seriesPacks: row?.seriesPacks ?? null,
  packCount: row?.packCount ?? null,
});

const sortByName = (key) => (a, b) =>
  String(a?.[key] ?? "").localeCompare(String(b?.[key] ?? ""), undefined, {
    sensitivity: "base",
  });

/**
 * @returns {{ current: Object, options: { projects, protocols, busTypes } }}
 * @throws {Error} when the project list could not be loaded at all
 */
export const buildVehicleConfigData = ({
  projectsRes,
  protocolsRes,
  busTypesRes,
  currentRes,
  vehicleSubType,
}) => {
  if (!isUsable(projectsRes)) {
    const err = new Error(projectsRes?.responseStatus?.msg);
    err.responseCode = projectsRes?.responseStatus?.code;
    throw err;
  }

  const projectRows = Array.isArray(projectsRes?.response?.data) ? projectsRes.response.data : [];
  const projects = projectRows
    .map(toProjectOption)
    .filter((p) => p.configID !== null)
    .sort(sortByName("configName"));

  const protocols = (isUsable(protocolsRes) ? getFilterRows(protocolsRes) : [])
    .map((row) => ({ protocolID: toNumberOrNull(row?.id), protocolName: row?.value ?? null }))
    .filter((p) => p.protocolID !== null)
    .sort(sortByName("protocolName"));

  const busTypes = [
    ...new Set(
      (isUsable(busTypesRes) ? getFilterRows(busTypesRes) : [])
        .map((row) => String(row?.value ?? "").trim())
        .filter(Boolean)
    ),
  ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }) || a.localeCompare(b));

  const currentRow = isUsable(currentRes) ? getFilterRows(currentRes)[0] : undefined;
  const configID = toNumberOrNull(currentRow?.value);
  const protocolID = toNumberOrNull(currentRow?.id);

  const project = projects.find((p) => p.configID === configID);
  const protocol = protocols.find((p) => p.protocolID === protocolID);
  const structure = project ? getStringStructure(project) : null;

  return {
    current: {
      configID,
      configName: project?.configName ?? null,
      ratedCapacitySystem: project?.ratedCapacitySystem ?? null,
      buildingBlockType: project?.buildingBlockType ?? null,
      stringCount: structure?.hasStrings ? structure.stringCount : null,
      packsPerString: structure ? structure.packsPerString : null,
      protocolID,
      protocolName: protocol?.protocolName ?? null,
      vehicleSubType: vehicleSubType ?? null,
    },
    options: { projects, protocols, busTypes },
  };
};
